import { useEffect, useState, useCallback } from "react";
import { Users as UsersIcon, Check, Ban, RotateCcw, Trash2, Loader2, ShieldCheck, Clock, UserCog, Radio, Mic, Phone } from "lucide-react";
import { toast } from "sonner";
import { api, errorMessage } from "../lib/api";
import { ConfirmDialog } from "../components/ConfirmDialog";

const STATUS = {
  approved: { label: "Attivo", cls: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30", icon: ShieldCheck },
  pending: { label: "In attesa", cls: "bg-amber-500/15 text-amber-300 border-amber-500/30", icon: Clock },
  suspended: { label: "Sospeso", cls: "bg-red-500/15 text-red-300 border-red-500/30", icon: Ban },
};

const FEATURE_LABEL = { sales_help: "Sales Help", mypailot: "MyPailot", chiamate: "Chiamate" };
const FEATURE_ICON = { sales_help: Radio, mypailot: Mic, chiamate: Phone };

// Interruttori dei moduli abilitati per un utente (l'admin li ha sempre tutti)
const FeatureToggles = ({ u, onToggle }) => (
  <div data-testid={`features-${u.email}`} className="flex flex-wrap gap-2">
    {Object.keys(FEATURE_LABEL).map((k) => {
      const on = u.features?.[k] !== false;
      const Icon = FEATURE_ICON[k];
      return (
        <button key={k} type="button" role="switch" aria-checked={on} data-testid={`feature-${k}-${u.email}`} onClick={() => onToggle(u, k)}
          className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
            on ? "border-primary/40 bg-primary/15 text-white" : "border-border text-muted-foreground line-through decoration-muted-foreground/60 hover:text-white"}`}>
          <Icon className="h-3 w-3" /> {FEATURE_LABEL[k]}
        </button>
      );
    })}
  </div>
);

export default function Users() {
  const [data, setData] = useState({ items: [], pending: 0, total: 0 });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [toDelete, setToDelete] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/users");
      setData(data);
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const act = async (id, action, okMsg) => {
    setBusy(id + action);
    try {
      await api.post(`/admin/users/${id}/${action}`);
      toast.success(okMsg);
      await load();
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setBusy(null);
    }
  };

  const doDelete = async () => {
    const id = toDelete.id;
    setBusy(id + "delete");
    try {
      await api.delete(`/admin/users/${id}`);
      toast.success("Utente e relativi dati eliminati");
      setToDelete(null);
      await load();
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setBusy(null);
    }
  };

  // Moduli per utente: aggiornamento ottimistico + salvataggio
  const toggleFeature = async (u, key) => {
    const next = { ...u.features, [key]: !u.features?.[key] };
    setData((d) => ({ ...d, items: d.items.map((x) => (x.id === u.id ? { ...x, features: next } : x)) }));
    try {
      await api.put(`/admin/users/${u.id}/features`, { [key]: next[key] });
      toast.success(`${FEATURE_LABEL[key]} ${next[key] ? "abilitato" : "disabilitato"} per ${u.email}`);
    } catch (e) {
      toast.error(errorMessage(e));
      await load();
    }
  };

  return (
    <div className="space-y-6" data-testid="users-page">
      <div className="flex items-center gap-3">
        <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary/15 border border-primary/30">
          <UsersIcon className="h-5 w-5 text-primary" />
        </div>
        <div>
          <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">Utenti</h1>
          <p className="text-sm text-muted-foreground">Approva le registrazioni, gestisci gli accessi e scegli quali moduli (Sales Help, MyPailot, Chiamate) vede ogni utente.</p>
        </div>
      </div>

      <div className="flex flex-wrap gap-3">
        <span data-testid="users-total" className="surface px-4 py-2 text-sm">Totale: <strong>{data.total}</strong></span>
        <span data-testid="users-pending" className="surface px-4 py-2 text-sm">In attesa: <strong className="text-amber-300">{data.pending}</strong></span>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20"><Loader2 className="h-6 w-6 spin-slow text-primary" /></div>
      ) : (
        <div className="space-y-3" data-testid="users-list">
          {data.items.map((u) => {
            const s = STATUS[u.status] || STATUS.pending;
            const SIcon = s.icon;
            return (
              <div key={u.id} data-testid={`user-row-${u.email}`} className="surface flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="truncate font-semibold">{u.name || u.email}</p>
                    {u.role === "admin" && (
                      <span className="inline-flex items-center gap-1 rounded-full border border-primary/30 bg-primary/15 px-2 py-0.5 text-[11px] font-medium text-primary">
                        <UserCog className="h-3 w-3" /> Admin
                      </span>
                    )}
                  </div>
                  <p className="truncate text-sm text-muted-foreground">{u.email}</p>
                  {u.role !== "admin" && <div className="mt-2"><FeatureToggles u={u} onToggle={toggleFeature} /></div>}
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <span className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-medium ${s.cls}`}>
                    <SIcon className="h-3 w-3" /> {s.label}
                  </span>
                  {u.role !== "admin" && (
                    <>
                      {u.status === "pending" && (
                        <button data-testid={`approve-${u.email}`} onClick={() => act(u.id, "approve", "Utente approvato")} disabled={busy === u.id + "approve"}
                          className="inline-flex items-center gap-1 rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-emerald-500 disabled:opacity-50">
                          <Check className="h-3.5 w-3.5" /> Approva
                        </button>
                      )}
                      {u.status === "approved" && (
                        <button data-testid={`suspend-${u.email}`} onClick={() => act(u.id, "suspend", "Utente sospeso")} disabled={busy === u.id + "suspend"}
                          className="inline-flex items-center gap-1 rounded-lg border border-border px-3 py-1.5 text-xs font-semibold text-muted-foreground transition-colors hover:bg-secondary hover:text-white disabled:opacity-50">
                          <Ban className="h-3.5 w-3.5" /> Sospendi
                        </button>
                      )}
                      {u.status === "suspended" && (
                        <button data-testid={`reactivate-${u.email}`} onClick={() => act(u.id, "reactivate", "Utente riattivato")} disabled={busy === u.id + "reactivate"}
                          className="inline-flex items-center gap-1 rounded-lg bg-primary px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-[#1D4ED8] disabled:opacity-50">
                          <RotateCcw className="h-3.5 w-3.5" /> Riattiva
                        </button>
                      )}
                      <button data-testid={`delete-${u.email}`} onClick={() => setToDelete(u)}
                        className="inline-flex items-center gap-1 rounded-lg border border-red-500/30 px-3 py-1.5 text-xs font-semibold text-red-300 transition-colors hover:bg-red-500/15">
                        <Trash2 className="h-3.5 w-3.5" /> Elimina
                      </button>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <ConfirmDialog
        open={!!toDelete}
        onCancel={() => setToDelete(null)}
        onConfirm={doDelete}
        title="Eliminare questo utente?"
        description={`Verranno eliminati definitivamente l'account «${toDelete?.email || ""}» e TUTTI i suoi dati (contenuti, knowledge base, call, voce). L'operazione non è reversibile.`}
        confirmLabel="Elimina definitivamente"
      />
    </div>
  );
}
